<template>
  <div class="contact-page">
    <Hero :title="data?.hero?.title" :description="data?.hero?.description">
      <template #image>
        <ElementsImage v-if="data?.hero?.image" v-bind="data.hero.image" loading="eager" />
      </template>
    </Hero>

    <ElementsContainer>
      <div class="contact row">
        <div class="lg:col-6 w-full lg:w-1/2">
          <div v-if="data?.info" class="contact-location">
            <div class="contact-location__map">
              <iframe :src="data.info.iframe" loading="lazy"></iframe>
            </div>
            <div class="contact-location__body">
              <div class="contact-location__body-heading">
                <div class="contact-location__body-title">{{ data.info.title }}</div>
                <div class="contact-location__body-description">{{ data.info.description }}</div>
              </div>
              <div class="contact-location__body-items">
                <a v-for="(item, index) in data.info.data" :key="index" :href="item.link" class="contact-location__body-item">
                  <Icon :name="`ri:${item.icon}`" v-if="item.icon" />
                  <span>{{ item.title }}</span>
                </a>
              </div>
              <a v-if="data.info.link" :href="data.info.link.href" class="contact-location__body-link" :target="data.info.link.target">
                {{ data.info.link.title }}
              </a>
            </div>
          </div>
        </div>

        <div class="lg:col-6 w-full lg:w-1/2 mt-10 lg:mt-0">
          <form class="contact-form" @submit.prevent="onSubmit">
            <div v-if="data?.form" class="contact-form__heading">
              <div class="contact-form__heading-title">{{ data.form.title }}</div>
              <div class="contact-form__heading-description">{{ data.form.description }}</div>
            </div>

            <div class="flex flex-wrap -mx-2 gap-y-4">
              <div v-if="form.error" class="w-full px-2">
                <div class="bg-red-100 text-red-700 p-4 rounded">{{ form.error }}</div>
              </div>
              <div v-if="form.success" class="w-full px-2">
                <div class="bg-green-100 text-green-700 p-4 rounded">{{ $t('contact.successMessage') }}</div>
              </div>

              <div class="w-full sm:w-1/2 px-2">
                <ElementsFormInput
                  v-model="formFields.name"
                  :disabled="form.status"
                  required
                  name="name"
                  v-bind="fields.name"
                  :error="formErrors.name"
                />
              </div>
              <div class="w-full sm:w-1/2 px-2">
                <ElementsFormInput
                  v-model="formFields.surname"
                  :disabled="form.status"
                  required
                  name="surname"
                  v-bind="fields.surname"
                  :error="formErrors.surname"
                />
              </div>
              <div class="w-full sm:w-1/2 px-2">
                <ElementsFormInput
                  v-model="formFields.email"
                  :disabled="form.status"
                  required
                  name="email"
                  type="email"
                  v-bind="fields.email"
                  :error="formErrors.email"
                />
              </div>
              <div class="w-full sm:w-1/2 px-2">
                <ElementsFormInput
                  v-model="formFields.phone_number"
                  :disabled="form.status"
                  required
                  name="phone_number"
                  type="tel"
                  minlength="10"
                  maxlength="20"
                  v-bind="fields.phone_number"
                  :error="formErrors.phone_number"
                />
              </div>
              <div class="w-full px-2">
                <select
                  v-model="formFields.subject"
                  :disabled="form.status"
                  required
                  name="subject"
                  class="w-full h-12 border px-4 rounded outline-none focus:border-black"
                >
                  <option value="" disabled selected>{{ $t('contact.selectSubject') }}</option>
                  <option v-for="opt in fields.subject?.attributes?.options || []" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="w-full px-2">
                <textarea
                  v-model="formFields.content"
                  :disabled="form.status"
                  required
                  name="content"
                  rows="7"
                  class="w-full border p-4 rounded outline-none focus:border-black resize-none"
                  :placeholder="fields.content?.placeholder"
                ></textarea>
              </div>
              <div class="w-full px-2 flex items-center">
                <ElementsFormCheckbox v-model="formFields.terms" :disabled="form.status" required name="terms" :error="formErrors.terms">
                  <span v-html="fields.terms?.label || $t('common.accept')"></span>
                </ElementsFormCheckbox>
              </div>
              <div class="w-full px-2">
                <ElementsButton :disabled="form.status" tag="button" type="submit">
                  {{ fields.submit?.label || $t('common.send') }}
                </ElementsButton>
              </div>
            </div>
          </form>
        </div>
      </div>
    </ElementsContainer>
  </div>
</template>

<script setup lang="ts">
  import fetchApi from "~/utils/fetchApi"

  const { data: responseData } = await useAsyncData("contact", () =>
    fetchApi<any>("contact", {
      wrappedByKey: "data",
      populate: {
        hero: { populate: "*" },
        info: { populate: "*" },
        form: { populate: "*" }
      }
    })
  )

  const data = computed(() => responseData.value || {})

  const fields = computed(() => {
    if (!data.value?.form?.field) return {}
    return data.value.form.field.reduce((acc: any, curr: any) => {
      const { name, ...rest } = curr
      acc[name] = rest
      return acc
    }, {})
  })

  const formFields = reactive({
    name: "",
    surname: "",
    email: "",
    phone_number: "",
    subject: "",
    content: "",
    terms: false
  })

  const formErrors = reactive<Record<string, string>>({})
  const form = reactive({
    status: false,
    error: "",
    success: false
  })

  const onSubmit = async () => {
    form.status = true
    form.error = ""
    form.success = false

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ ...formFields, recaptcha: "token-placeholder" })
      })

      if (response.ok) {
        form.success = true
        // Reset form
        Object.keys(formFields).forEach((k) => ((formFields as any)[k] = ""))
      } else {
        const result = await response.json()
        form.error = result.message || "Bir hata oluştu."
        if (result.data) {
          Object.assign(formErrors, result.data)
        }
      }
    } catch (e: any) {
      form.error = e.message || "Bağlantı hatası"
    } finally {
      form.status = false
    }
  }

  const { t } = useI18n()

  useSeoMeta({
    title: () => data.value?.hero?.title || t("menu.contact"),
    description: () => data.value?.hero?.description || ""
  })
</script>

<style lang="scss" scoped>
  .contact {
    --statu-gutter-x: 80px;

    &-location {
      @apply flex flex-col w-full overflow-hidden;

      &__map {
        @apply flex w-full h-auto relative;
        padding-top: 60%;

        iframe {
          @apply absolute inset-0 w-full h-full;
        }
      }

      &__body {
        @apply flex flex-col p-8 gap-9 text-white justify-between h-full;
        background: rgba(#282828, 0.98);

        &-heading {
          @apply flex flex-col gap-1;
        }

        &-title {
          @apply font-display text-2xl font-medium tracking-wider uppercase;
        }

        &-description {
          @apply text-white/60 text-base leading-5.5 font-medium;
        }

        &-items {
          @apply flex flex-col gap-4;
        }

        &-item {
          @apply flex items-center gap-4 text-white/80 transition-all duration-200;

          :deep(svg) {
            @apply text-8 flex-none;
          }

          span {
            @apply text-base font-medium tracking-wide;
          }
        }

        &-link {
          @apply text-white/60 text-sm tracking-wider uppercase font-bold underline underline-offset-5 transition-all duration-200;
        }
      }
    }
    &-form {
      @apply flex flex-col gap-14;

      &__heading {
        @apply flex flex-col gap-1 text-center;

        &-title {
          @apply font-display text-2xl font-medium tracking-wider uppercase text-black;
        }

        &-description {
          @apply text-black/50 text-base leading-5.5 font-medium text-balance;
        }
      }
    }

    @include media("xl") {
      --statu-gutter-x: 40px;
    }
    @include media("lg") {
      --statu-gutter-x: 0px;
      --statu-gutter-y: 40px;

      &-location {
        &__body {
          @apply p-6 gap-6;

          &-heading {
            @apply gap-1;
          }
          &-title {
            @apply text-xl;
          }
          &-description {
            @apply text-sm;
          }
          &-items {
            @apply gap-3;
          }
          &-item {
            @apply gap-3;

            :deep(svg) {
              @apply text-6;
            }
            span {
              @apply text-sm;
            }
          }
          &-link {
            @apply text-xs;
          }
        }
      }
      &-form {
        @apply gap-8;

        &__heading {
          &-title {
            @apply text-xl;
          }
          &-description {
            @apply text-sm;
          }
        }
      }
    }
    @include media("md") {
      --statu-gutter-x: 0px;
      --statu-gutter-y: 24px;

      &-location {
        &__body {
          @apply p-4 gap-5;

          &-heading {
            @apply gap-0.5;
          }
          &-title {
            @apply text-lg;
          }
          &-description {
            @apply text-sm;
          }
          &-items {
            @apply gap-2.5;
          }
          &-item {
            @apply gap-2.5;

            :deep(svg) {
              @apply text-5;
            }
            span {
              @apply text-xs;
            }
          }
          &-link {
            @apply text-xs;
          }
        }
      }
      &-form {
        @apply gap-4;

        &__heading {
          @apply gap-0.5;

          &-title {
            @apply text-lg;
          }
          &-description {
            @apply text-sm;
          }
        }
      }
    }
  }
</style>
